import * as React from 'react';
import { t } from '@reos/i18n';
import OlMap from 'ol/Map.js';
import View from 'ol/View.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Feature from 'ol/Feature.js';
import Polygon from 'ol/geom/Polygon.js';
import CircleGeom from 'ol/geom/Circle.js';
import Draw from 'ol/interaction/Draw.js';
import type { Layer } from 'ol/layer.js';
import type { MapBrowserEvent } from 'ol/index.js';
import { Fill, Stroke, Style } from 'ol/style.js';
import { registerSjtskProjection, wgs84ToSjtsk } from '../../lib/parcel-map-crs.js';
import { fetchClientMapLayers } from '../../lib/parcel-map-api.js';
import type { TerritoryDto } from '../../lib/acquisition-radar-api.js';
import { buildLayer } from '../ParcelMap.js';
import 'ol/ol.css';

/**
 * Krok AKV1 — mapa uzemi akvizice. NENI to nova mapa: stejna OpenLayers mapa
 * v S-JTSK (EPSG:5514) jako mapova karta zakazky (krok M1) — projekce
 * `registerSjtskProjection`, podkladove vrstvy z tehoz registru
 * (`fetchClientMapLayers`) a tytez `buildLayer`. Pridava jen kresleni
 * (`ol/interaction/Draw`) polygonu a vyber stredu okruhu.
 *
 * Souradnice jdou ven v 5514 tak, jak je mapa ma — prevod do WGS84 pro radar
 * dela SERVER (zadani AKV1). Ulozena uzemi (WGS84 z radaru) se pro zobrazeni
 * prevadeji zpet tymz `wgs84ToSjtsk`.
 *
 * Barvy obrysu se NEPISOU: berou se z tokenu tematu (`--rh-accent`) za behu.
 */

export type SjtskPair = readonly [number, number];

export interface TerritoryMapProps {
  /** `polygon` = kresleni oblasti, `radius` = klik vybere stred, `view` = jen zobrazit. */
  readonly mode: 'polygon' | 'radius' | 'view';
  readonly radiusKm?: number | undefined;
  readonly center?: SjtskPair | null | undefined;
  readonly polygon?: readonly SjtskPair[] | null | undefined;
  /** Ulozena uzemi kancelare (kontext pri kresleni noveho). */
  readonly territories?: readonly TerritoryDto[] | undefined;
  readonly onPolygon?: ((ring: SjtskPair[]) => void) | undefined;
  readonly onCenter?: ((point: SjtskPair) => void) | undefined;
}

/** Stred Ceska — vychozi pohled, kdyz jeste neni nic nakresleno. */
const CZECHIA_CENTER = wgs84ToSjtsk({ lat: 49.8, lon: 15.5 });
const DEFAULT_ZOOM = 7;
/** Podklad uzemi: ortofoto a katastr; ostatni vrstvy registru patri parcelam. */
const BASE_LAYER_IDS = new Set(['orthophoto', 'cadastral']);

function themeColor(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function territoryStyle(): Style {
  const accent = themeColor('--rh-accent');
  return new Style({
    stroke: new Stroke({ color: accent, width: 2 }),
    fill: new Fill({ color: themeColor('--rh-accent-soft') }),
  });
}

function savedFeatures(territories: readonly TerritoryDto[]): Feature[] {
  const out: Feature[] = [];
  for (const territory of territories) {
    if (territory.polygon !== null) {
      const ring = territory.polygon.map(([lon, lat]) => {
        const p = wgs84ToSjtsk({ lat, lon });
        return [p.x, p.y];
      });
      out.push(new Feature(new Polygon([ring])));
    } else if (territory.center !== null && territory.radiusKm !== null) {
      const c = wgs84ToSjtsk(territory.center);
      out.push(new Feature(new CircleGeom([c.x, c.y], territory.radiusKm * 1000)));
    }
  }
  return out;
}

export function TerritoryMap({
  mode,
  radiusKm,
  center,
  polygon,
  territories = [],
  onPolygon,
  onCenter,
}: TerritoryMapProps): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<OlMap | null>(null);
  const draftSource = React.useRef(new VectorSource());
  const savedSource = React.useRef(new VectorSource());
  const [baseLayers, setBaseLayers] = React.useState<readonly Layer[]>([]);
  const onPolygonRef = React.useRef(onPolygon);
  const onCenterRef = React.useRef(onCenter);
  onPolygonRef.current = onPolygon;
  onCenterRef.current = onCenter;

  React.useEffect(() => {
    let cancelled = false;
    fetchClientMapLayers()
      .then((layers) => {
        if (cancelled) return;
        setBaseLayers(
          layers.filter((l) => BASE_LAYER_IDS.has(l.id)).map(buildLayer).filter((l): l is Layer => l !== null),
        );
      })
      .catch(() => {
        // Bez podkladu se da kreslit dal (obrysy uzemi zustanou) — mapa se nevzdava.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (containerRef.current === null) return;
    const projection = registerSjtskProjection();
    const style = territoryStyle();
    const map = new OlMap({
      target: containerRef.current,
      layers: [
        ...baseLayers,
        new VectorLayer({ source: savedSource.current, style }),
        new VectorLayer({ source: draftSource.current, style }),
      ],
      view: new View({ projection, center: [CZECHIA_CENTER.x, CZECHIA_CENTER.y], zoom: DEFAULT_ZOOM }),
    });
    mapRef.current = map;
    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, [baseLayers]);

  // Ulozena uzemi jako kontext.
  React.useEffect(() => {
    savedSource.current.clear();
    savedSource.current.addFeatures(savedFeatures(territories));
  }, [territories]);

  // Rozpracovane uzemi (polygon nebo okruh) — kresli se z props, mapa je jen zobrazuje.
  React.useEffect(() => {
    draftSource.current.clear();
    if (polygon !== null && polygon !== undefined && polygon.length >= 3) {
      draftSource.current.addFeature(new Feature(new Polygon([polygon.map((p) => [p[0], p[1]])])));
    }
    if (center !== null && center !== undefined && radiusKm !== undefined && radiusKm > 0) {
      draftSource.current.addFeature(new Feature(new CircleGeom([center[0], center[1]], radiusKm * 1000)));
    }
  }, [polygon, center, radiusKm]);

  // Interakce podle rezimu.
  React.useEffect(() => {
    const map = mapRef.current;
    if (map === null) return;
    if (mode === 'polygon') {
      const draw = new Draw({ source: new VectorSource(), type: 'Polygon' });
      draw.on('drawend', (event) => {
        const geometry = event.feature.getGeometry();
        if (!(geometry instanceof Polygon)) return;
        const ring = (geometry.getCoordinates()[0] ?? []).map((c) => [c[0] ?? 0, c[1] ?? 0] as const);
        onPolygonRef.current?.(ring);
      });
      map.addInteraction(draw);
      return () => {
        map.removeInteraction(draw);
      };
    }
    if (mode === 'radius') {
      const handler = (event: MapBrowserEvent): void => {
        const [x = 0, y = 0] = event.coordinate;
        onCenterRef.current?.([x, y]);
      };
      map.on('singleclick', handler);
      return () => {
        map.un('singleclick', handler);
      };
    }
    return undefined;
  }, [mode, baseLayers]);

  const hint =
    mode === 'polygon'
      ? t('webAdmin.acquisitionRadar.map.hintPolygon', { locale: 'cs' })
      : mode === 'radius'
        ? t('webAdmin.acquisitionRadar.map.hintRadius', { locale: 'cs' })
        : undefined;

  return (
    <div className="rh-akv-map" data-testid="akv-territory-map">
      <div
        ref={containerRef}
        className="rh-akv-map__canvas"
        role="application"
        aria-label={t('webAdmin.acquisitionRadar.map.ariaLabel', { locale: 'cs' })}
      />
      {hint !== undefined ? <p className="rh-akv-hint">{hint}</p> : null}
    </div>
  );
}
